import React, { useEffect } from 'react';
import { format } from 'date-fns';
import CustomSelect from '../CustomSelect';
import { ScheduleFormData, RecurringPattern, WeekdayType } from '../../types/schedule';

interface ScheduleFormProps {
  formData: ScheduleFormData;
  setFormData: React.Dispatch<React.SetStateAction<ScheduleFormData>>;
  isRecurring: boolean;
  setIsRecurring: React.Dispatch<React.SetStateAction<boolean>>;
  recurringPattern: RecurringPattern;
  setRecurringPattern: React.Dispatch<React.SetStateAction<RecurringPattern>>;
  recurringUntil: string;
  setRecurringUntil: React.Dispatch<React.SetStateAction<string>>;
  recurringWeekdays: WeekdayType[];
  setRecurringWeekdays: React.Dispatch<React.SetStateAction<WeekdayType[]>>;
  isAllDay: boolean;
  setIsAllDay: React.Dispatch<React.SetStateAction<boolean>>;
  isHoliday: boolean;
  setIsHoliday: React.Dispatch<React.SetStateAction<boolean>>;
  holidayEndDate: string;
  setHolidayEndDate: React.Dispatch<React.SetStateAction<string>>;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}

const WEEKDAYS: { value: WeekdayType; label: string; short: string }[] = [
  { value: 'monday', label: '월요일', short: '월' },
  { value: 'tuesday', label: '화요일', short: '화' },
  { value: 'wednesday', label: '수요일', short: '수' },
  { value: 'thursday', label: '목요일', short: '목' },
  { value: 'friday', label: '금요일', short: '금' },
  { value: 'saturday', label: '토요일', short: '토' },
  { value: 'sunday', label: '일요일', short: '일' }
];

// 현재 시간에서 가장 가까운 00/30분으로 반올림
const getNearestHalfHour = (): string => {
  const now = new Date();
  const minutes = now.getMinutes();
  const hours = now.getHours();

  let roundedMinutes: number;
  let roundedHours = hours;

  if (minutes < 15) {
    roundedMinutes = 0;
  } else if (minutes < 45) {
    roundedMinutes = 30;
  } else {
    roundedMinutes = 0;
    roundedHours = hours + 1;
    if (roundedHours >= 24) roundedHours = 0;
  }

  return `${String(roundedHours).padStart(2, '0')}:${String(roundedMinutes).padStart(2, '0')}`;
};

// 시작 시간에서 1시간 추가
const addOneHour = (time: string): string => {
  if (!time) return '';
  const [hours, minutes] = time.split(':').map(Number);
  let newHours = hours + 1;
  if (newHours >= 24) newHours = 23;
  return `${String(newHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const ScheduleForm: React.FC<ScheduleFormProps> = ({
  formData,
  setFormData,
  isRecurring,
  setIsRecurring,
  recurringPattern,
  setRecurringPattern,
  recurringUntil,
  setRecurringUntil,
  recurringWeekdays,
  setRecurringWeekdays,
  isAllDay,
  setIsAllDay,
  isHoliday,
  setIsHoliday,
  holidayEndDate,
  setHolidayEndDate,
  onSubmit,
  onCancel
}) => {
  // 요일 토글 핸들러
  const toggleWeekday = (day: WeekdayType) => {
    if (recurringWeekdays.includes(day)) {
      setRecurringWeekdays(recurringWeekdays.filter(d => d !== day));
    } else {
      setRecurringWeekdays([...recurringWeekdays, day]);
    }
  };
  // 초기 시간 설정
  useEffect(() => {
    if (!formData.startTime) {
      const defaultStart = getNearestHalfHour();
      const defaultEnd = addOneHour(defaultStart);
      setFormData(prev => ({
        ...prev,
        startTime: defaultStart,
        endTime: defaultEnd
      }));
    }
  }, []);

  // 시작 시간 변경 시 종료 시간 자동 설정
  const handleStartTimeChange = (newStartTime: string) => {
    const newEndTime = addOneHour(newStartTime);
    setFormData({
      ...formData,
      startTime: newStartTime,
      endTime: newEndTime
    });
  };

  // 휴일 = 종일. 해제하면 시간 입력(개인일정)으로 복귀.
  const handleHolidayChange = (checked: boolean) => {
    setIsHoliday(checked);
    setIsAllDay(checked);
    if (checked) setIsRecurring(false);
  };

  return (
    <div className="schedule-form-section">
      <h2>{isHoliday ? '휴일 지정' : '개인 일정 등록'}</h2>
      <form onSubmit={onSubmit} className="schedule-form">
        {/* 제목 */}
        <div className="form-group">
          <input
            type="text"
            id="title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="form-input"
            placeholder={isHoliday ? '휴일 제목 (예: 설 연휴)' : '일정 제목'}
            required
          />
        </div>

        {/* 휴일/반복 옵션 (종일 개인일정은 '휴일 지정'으로 일원화 — 종일 체크박스 제거) */}
        <div className="form-group form-options-row">
          <label className="form-checkbox-label holiday-option">
            <input
              type="checkbox"
              checked={isHoliday}
              onChange={(e) => handleHolidayChange(e.target.checked)}
            />
            <span>휴일 지정</span>
          </label>
          {!isAllDay && (
            <label className="form-checkbox-label">
              <input
                type="checkbox"
                checked={isRecurring}
                onChange={(e) => setIsRecurring(e.target.checked)}
              />
              <span>반복</span>
            </label>
          )}
        </div>

        {/* 휴일인 경우 - 날짜 범위 선택 */}
        {isHoliday ? (
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">시작일</label>
              <input
                type="date"
                value={formData.scheduleDate}
                onChange={(e) => {
                  setFormData({ ...formData, scheduleDate: e.target.value });
                  if (!holidayEndDate || e.target.value > holidayEndDate) {
                    setHolidayEndDate(e.target.value);
                  }
                }}
                min={format(new Date(), 'yyyy-MM-dd')}
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">종료일</label>
              <input
                type="date"
                value={holidayEndDate}
                onChange={(e) => setHolidayEndDate(e.target.value)}
                min={formData.scheduleDate || format(new Date(), 'yyyy-MM-dd')}
                className="form-input"
                required
              />
            </div>
          </div>
        ) : isRecurring ? (
          /* 반복 일정인 경우 */
          <>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">반복</label>
                <CustomSelect
                  options={[
                    { value: 'weekly', label: '매주' },
                    { value: 'daily', label: '매일' },
                    { value: 'monthly', label: '매월' }
                  ]}
                  value={recurringPattern}
                  onChange={(value) => setRecurringPattern(value as RecurringPattern)}
                  placeholder="선택"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">마감일</label>
                <input
                  type="date"
                  className="form-input"
                  value={recurringUntil}
                  onChange={(e) => setRecurringUntil(e.target.value)}
                  min={format(new Date(), 'yyyy-MM-dd')}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <div className="weekday-selector">
                {WEEKDAYS.map((day) => (
                  <button
                    key={day.value}
                    type="button"
                    className={`weekday-btn ${recurringWeekdays.includes(day.value) ? 'active' : ''}`}
                    onClick={() => toggleWeekday(day.value)}
                  >
                    {day.short}
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          /* 단일 일정인 경우 - 날짜 선택 */
          <div className="form-group">
            <input
              type="date"
              id="scheduleDate"
              value={formData.scheduleDate}
              onChange={(e) => setFormData({ ...formData, scheduleDate: e.target.value })}
              min={format(new Date(), 'yyyy-MM-dd')}
              className="form-input"
              required
            />
          </div>
        )}

        {/* 시간 선택 - 종일이 아닌 경우에만 표시 */}
        {!isAllDay && (
          <div className="form-row">
            <div className="form-group">
              <input
                type="time"
                id="startTime"
                value={formData.startTime}
                onChange={(e) => handleStartTimeChange(e.target.value)}
                className="form-input"
                step="1800"
                required
              />
            </div>
            <div className="form-group">
              <input
                type="time"
                id="endTime"
                value={formData.endTime}
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                className="form-input"
                step="1800"
                required
              />
            </div>
          </div>
        )}

        {/* 설명 - 휴일이 아닌 경우에만 표시 */}
        {!isHoliday && (
          <div className="form-group">
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="form-textarea"
              placeholder="설명 (선택)"
              rows={2}
            />
          </div>
        )}

        {/* 버튼 */}
        <div className="form-actions">
          <button type="submit" className="btn btn-primary">
            {isHoliday ? '휴일 등록' : '등록'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-white"
          >
            취소
          </button>
        </div>
      </form>
    </div>
  );
};

export default ScheduleForm;
